import React, { useEffect } from 'react';
import { StoreFinderRedirector } from '@thd-nucleus/app-render';
import { QueryContext, extend } from '@thd-nucleus/data-sources';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { CardCarousel, EmtCarousel } from '@thd-olt-component-react/card-carousel';
import {
  GrandOpeningContainer,
  GardenCenterContent,
  GardenCenterFAQ,
  IdeasCarousel,
  PromoPod
} from '@thd-olt-component-react/emt-generated-content';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { GardenCenterMetadata } from '@thd-olt-component-react/metadata';
import { PopularCategories } from '@thd-olt-component-react/related-search';
import { useLocation, useParams } from '@thd-olt-component-react/router';
import {
  StoreHero,
  StoreHeroDetails,
  StoreHeroHeader,
  StoreHeroServices
} from '@thd-olt-component-react/store-hero';

import { BrandsPod } from '../../shared-components/BrandsPod';
import { Calculators } from '../../shared-components/Calculators';
import { NearbyStores } from '../../shared-components/NearbyStores';
import { RecommendationsPod } from '../../shared-components/RecommendationsPod';
import { StoreFinderBreadcrumbs } from '../../shared-components/StoreFinderBreadcrumbs';

export const GardenCenter = () => {
  declareContexts([QueryContext], { freeze: true });

  const {
    storeNumber, storeZip, state, name, city
  } = useParams();
  const path = useLocation();

  const phoneNumbersToInclude = {
    services: false,
    store: true,
    pro: true,
    tool: true
  };

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('store-finder.garden-center-page-load', {
      storeNumber,
      storeZip,
      state,
      name,
      city
    });
  }, [storeNumber]);

  return (
    <>
      <ErrorBoundary name="sf-gardenCenter-redirector">
        <StoreFinderRedirector />
      </ErrorBoundary>

      <ErrorBoundary name="garden-center-metadata">
        <GardenCenterMetadata storeId={storeNumber} />
      </ErrorBoundary>

      <div className="sui-max-w-screen-2xl sui-m-auto">
        <div className="sui-flex sui-flex-col sui-px-2 2xl:sui-px-0">
          <ErrorBoundary name="garden-center-breadcrumbs">
            <StoreFinderBreadcrumbs
              includeDirectory
              pageName="Garden Center"
            />
          </ErrorBoundary>
          <ErrorBoundary name="garden-center-grand-opening-pod">
            <GrandOpeningContainer
              contentId={storeNumber}
              pageName="gardenCenter"
            />
          </ErrorBoundary>
          <ErrorBoundary name="garden-center-header">
            <StoreHero
              storeId={storeNumber}
            >
              <StoreHeroHeader
                hideRating
                label="Garden Center at "
              />
              <section className="sui-grid sui-grid-cols-12 sui-gap-4">
                <div className="sui-col-span-12 lg:sui-col-span-8">
                  <StoreHeroServices
                    type="garden"
                    storeZip={storeZip}
                  />
                </div>
                <div className="sui-col-span-12 lg:sui-col-span-4">
                  <StoreHeroDetails
                    phoneNumbers={phoneNumbersToInclude}
                    showStoreDetailsButton
                    title="Garden Center"
                    contentId={storeNumber}
                    pageName="gardenCenter"
                  />
                </div>
              </section>
            </StoreHero>
          </ErrorBoundary>

          <section className="sui-flex sui-flex-col sui-gap-y-8 sui-mt-8">
            <ErrorBoundary name="garden-center-promo-pod">
              <PromoPod contentId={storeNumber} pageName="gardenCenter" />
            </ErrorBoundary>

            <ErrorBoundary name="garden-center-emt-carousel">
              <EmtCarousel
                contentId={storeNumber}
                pageName="gardenCenter"
                headerTitle="Popular at Your Garden Center"
              />
            </ErrorBoundary>

            <ErrorBoundary name="garden-center-recommendations-pod">
              <RecommendationsPod storeNumber={storeNumber} />
            </ErrorBoundary>

            <ErrorBoundary name="garden-center-recommendations-carousel">
              <CardCarousel entryId="3iD5OG0BbHHd6apMhNNvSf" />
            </ErrorBoundary>

            <ErrorBoundary name="garden-center-project-calculators">
              <Calculators trackingParam="?emt=gcCal" />
            </ErrorBoundary>

            <ErrorBoundary name="garden-center-brands-pod">
              <BrandsPod
                title="Shop Outdoor and Garden Brands"
                trackingParam="?emt=gcBr"
              />
            </ErrorBoundary>

            <ErrorBoundary name="emt-garden-center-faq">
              <GardenCenterFAQ
                storeId={storeNumber}
                title="Frequently Asked Questions About Gardening"
              />
            </ErrorBoundary>

            <ErrorBoundary name="garden-center-carousel-ideas">
              <IdeasCarousel
                contentId={storeNumber}
                pageName="gardenCenter"
                sectionTitle="Garden Project Ideas"
              />
            </ErrorBoundary>

            <ErrorBoundary name="emt-garden-center-content">
              <GardenCenterContent storeId={storeNumber} />
            </ErrorBoundary>

            <ErrorBoundary name="garden-center-nearby-stores">
              <Hydrator
                id="garden-center-nearby-stores-map"
                scrollBuffer={800}
              >
                <NearbyStores
                  storeNumber={storeNumber}
                  omitStoreNumber={storeNumber}
                  pageType="gardenCenter"
                />
              </Hydrator>
            </ErrorBoundary>

            <ErrorBoundary name="garden-center-popular-categories">
              <PopularCategories
                contentId={storeNumber}
                pageType="gardenCenter"
              />
            </ErrorBoundary>

            <ErrorBoundary name="garden-center-explore-more">
              <EmtLinks path={path.pathname} />
            </ErrorBoundary>
          </section>
        </div>
      </div>
    </>
  );
};

GardenCenter.displayName = 'GardenCenter';
GardenCenter.propTypes = {};

GardenCenter.dataModel = extend(
  CardCarousel,
  Breadcrumbs,
  EmtCarousel,
  EmtLinks,
  GardenCenterContent,
  GardenCenterFAQ,
  GardenCenterMetadata,
  GrandOpeningContainer,
  IdeasCarousel,
  NearbyStores,
  PromoPod,
  PopularCategories,
  RecommendationsPod,
  StoreFinderRedirector,
  StoreHero,
);
